import React, { useEffect, useRef, useState } from 'react';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TablePagination from 'CommonComponents/TablePagination';
import TableSearchInput from 'CommonComponents/TableSearchInput';

// Read-only, server-side searchable/filterable/paginated list for the Sessions
// codeset tab. Sessions can exceed 200 rows, so search, filters and paging all
// drive the server (codeset-sessions.index) rather than loading everything.
export default function CodesetSessionsList({ fetchUrl }) {
    const [rows, setRows] = useState([]);
    const [filterOptions, setFilterOptions] = useState({ events: [], venues: [], types: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ event_id: '', venue_id: '', schedule_type_id: '', date: '' });
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({ total: 0, last_page: 1, current_page: 1 });

    const timeoutRef = useRef(null);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(fetchSessions, 300);

        return () => timeoutRef.current && clearTimeout(timeoutRef.current);
    }, [search, filters, page]);

    const fetchSessions = () => {
        setIsLoading(true);

        axios
            .get(fetchUrl, {
                params: {
                    search,
                    page,
                    event_id: filters.event_id,
                    venue_id: filters.venue_id,
                    schedule_type_id: filters.schedule_type_id,
                    date: filters.date,
                },
            })
            .then(({ data }) => {
                setRows(data.data || []);
                setMeta({ total: data.total, last_page: data.last_page, current_page: data.current_page });
                if (data.filter_options) {
                    setFilterOptions(data.filter_options);
                }
            })
            .finally(() => setIsLoading(false));
    };

    const updateFilter = (key, value) => {
        setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const onSearch = (e) => {
        setPage(1);
        setSearch(e.target.value);
    };

    return (
        <div>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 tw-mb-4">
                <div className="tw-w-full sm:tw-w-80">
                    <TableSearchInput searchValue={search} handleChange={onSearch} />
                </div>
                <div className="tw-text-sm tw-text-charcoal-50">{$t('{{count}} sessions · synced from Events module', { count: meta.total ?? 0 })}</div>
            </div>

            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-mb-4">
                <FilterSelect
                    label={$t('Event')}
                    value={filters.event_id}
                    options={filterOptions.events}
                    onChange={(value) => updateFilter('event_id', value)}
                />
                <FilterSelect
                    label={$t('Venue')}
                    value={filters.venue_id}
                    options={filterOptions.venues}
                    onChange={(value) => updateFilter('venue_id', value)}
                />
                <FilterSelect
                    label={$t('Type')}
                    value={filters.schedule_type_id}
                    options={filterOptions.types}
                    onChange={(value) => updateFilter('schedule_type_id', value)}
                />
                <input
                    type="date"
                    aria-label={$t('Date')}
                    value={filters.date}
                    onChange={(e) => updateFilter('date', e.target.value)}
                    className="tw-border-2 tw-border-grey tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-bg-white"
                />
            </div>

            {isLoading ? (
                <LoadingSpinner />
            ) : rows.length ? (
                <>
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    <th>{$t('Code')}</th>
                                    <th>{$t('Event')}</th>
                                    <th>{$t('Venue')}</th>
                                    <th>{$t('Date')}</th>
                                    <th>{$t('Time')}</th>
                                    <th>{$t('Session type')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id}>
                                        <td className="tw-font-medium tw-text-blue">{row.code}</td>
                                        <td>{row.event}</td>
                                        <td>{row.venue || '—'}</td>
                                        <td>{formatDate(row.date)}</td>
                                        <td>{formatTimeRange(row.start_time, row.end_time)}</td>
                                        <td>{row.session_type}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {meta.last_page > 1 && (
                        <div className="tw-flex tw-justify-end tw-mt-4">
                            <TablePagination lastPage={meta.last_page} currentPage={meta.current_page} pageChange={({ selected }) => setPage(selected + 1)} />
                        </div>
                    )}
                </>
            ) : (
                <div className="tw-text-center tw-text-charcoal-50 tw-py-10">{$t('No sessions found')}</div>
            )}
        </div>
    );
}

const FilterSelect = ({ label, value, options, onChange }) => (
    <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="tw-border-2 tw-border-grey tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-bg-white"
    >
        <option value="">{$t('{{label}}: All', { label })}</option>
        {(options || []).map((option) => (
            <option key={option.id} value={option.id}>
                {option.name}
            </option>
        ))}
    </select>
);

function formatDate(date) {
    if (!date) return '—';

    return date;
}

// start_time/end_time arrive as datetime strings (e.g. "2025-06-01 18:00:00");
// render just the HH:MM range.
function formatTimeRange(start, end) {
    const startTime = extractTime(start);
    const endTime = extractTime(end);

    if (!startTime && !endTime) return '—';

    return `${startTime}–${endTime}`;
}

function extractTime(value) {
    if (!value) return '';

    const match = String(value).match(/(\d{2}):(\d{2})/);

    return match ? `${match[1]}:${match[2]}` : '';
}
